import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    Card,
    CardContent,
    CardMedia,
    Typography,
    Box,
    IconButton,
    Stack,
    Chip,
    useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/system";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import StarIcon from "@mui/icons-material/Star";

const StyledCard = styled(Card)(({ theme }) => ({
    width: "100%",
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
        boxShadow: theme.shadows[6],
    },
    cursor: "pointer",
}));

const NavigationButton = styled(IconButton)(() => ({
    color: "white",
    backgroundColor: "rgba(255,255,255,0.3)",
    borderRadius: "50%",
    "&:hover": {
        backgroundColor: "rgba(255,255,255,0.5)",
    },
}));

const ImageContainer = styled(Box)({
    position: "relative",
    "&:hover .navigation-buttons": {
        opacity: 1,
    },
    "&:hover .status-chip": {
        opacity: 1,
    },
});

const NavigationButtonsContainer = styled(Box)({
    position: "absolute",
    top: "50%",
    left: 4,
    right: 4,
    zIndex: 2,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 1,
    transform: "translateY(-50%)",
    opacity: 0,
    transition: "opacity 0.2s ease-in-out",
});

const StatusChip = styled(Box)({
    position: "absolute",
    top: 4,
    left: 4,
    zIndex: 2,
    transition: "opacity 0.2s ease-in-out",
    opacity: 0
})

const ShortTermRentalCard = ({
    images,
    title,
    subtitle,
    bedrooms,
    beds,
    baths,
    guests,
    rating,
    reviewsCount,
    location,
    status,
    url,
}) => {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const handlePrevImage = (event) => {
        event.stopPropagation();
        setCurrentImageIndex((prevIndex) =>
            prevIndex > 0 ? prevIndex - 1 : images.length - 1
        );
    };

    const handleNextImage = (event) => {
        event.stopPropagation();
        setCurrentImageIndex((prevIndex) =>
            prevIndex < images.length - 1 ? prevIndex + 1 : 0
        );
    };

    const handleClick = () => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <StyledCard onClick={handleClick}>
            <ImageContainer>
                <CardMedia
                    component="img"
                    height="300"
                    image={images[currentImageIndex]}
                    alt={`Listing image ${currentImageIndex + 1}`}
                />
                <StatusChip className="status-chip">
                    <Chip label={status} color="primary" size="small" />
                </StatusChip>

                <NavigationButtonsContainer className="navigation-buttons">
                    <NavigationButton onClick={handlePrevImage}>
                        <ArrowBackIosNewIcon />
                    </NavigationButton>
                    <NavigationButton onClick={handleNextImage}>
                        <ArrowForwardIosIcon />
                    </NavigationButton>
                </NavigationButtonsContainer>
            </ImageContainer>
            <CardContent>
                <Stack spacing={1}>
                    <Typography variant="h6" component="div" fontWeight="700">
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {subtitle}
                    </Typography>
                    <Stack direction="row" spacing={isMobile ? 0.5 : 1.5}>
                        <Typography variant="body2" color="text.secondary">
                            {beds} Bedrooms
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            •
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {beds} Bed
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            •
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {baths} Bath
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            •
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {guests} Guests
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
                        <StarIcon fontSize="small" color="primary" />
                        <Typography variant="body2" color="text.secondary">
                            {rating}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            •
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            ({reviewsCount} reviews)
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        {location}
                    </Typography>
                </Stack>
            </CardContent>
        </StyledCard>
    );
};

ShortTermRentalCard.propTypes = {
    images: PropTypes.arrayOf(PropTypes.string).isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    bedrooms: PropTypes.number.isRequired,
    beds: PropTypes.number.isRequired,
    baths: PropTypes.number.isRequired,
    guests: PropTypes.number.isRequired,
    rating: PropTypes.number.isRequired,
    reviewsCount: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
};

export default ShortTermRentalCard;
